import cx from 'classnames';
import React from 'react';
import { NOD_CAMPAIGN_ENABLED_USER_IDS } from 'types/campaign';
import { EEventType } from 'types/event-type';

import { ButtonBase, Dialog } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ArrowDropDownRoundedIcon from '@material-ui/icons/ArrowDropDownRounded';

import { useAuthDataContext } from 'components/AuthDataProvider/AuthDataProvider';
import { useDataFilterContext } from 'components/DataMap/DataFilterProvider';
import EventTypeBadge from 'components/EventTypeBadge';
import SimpleModalHeader from 'components/SimpleModalHeader/SimpleModalHeader';

const useStyles = makeStyles((theme) => ({
  trigger: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    minHeight: '40px',
    padding: '6px 8px 6px 10px',
    border: `1px solid ${theme.palette.lightGray.main}`,
    borderRadius: '8px',
    backgroundColor: theme.palette.white.main,
    transition: 'border-color 200ms ease, box-shadow 200ms ease',

    '&:hover': {
      borderColor: theme.palette.primary.main,
      boxShadow: '0 1px 3px rgba(32, 37, 50, 0.18)',
    },

    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: '2px',
    },
  },
  triggerIcon: {
    color: theme.palette.darkGray.main,
  },
  options: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: '12px',
    padding: '4px 20px 20px',

    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '1fr',
    },
  },
  option: {
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minWidth: '220px',
    padding: '18px',
    border: `2px solid ${theme.palette.lightGray.main}`,
    borderRadius: '8px',
    backgroundColor: theme.palette.white.main,
    textAlign: 'center',
    transition: 'border-color 200ms ease, box-shadow 200ms ease',

    '&:hover': {
      borderColor: theme.palette.primary.main,
      boxShadow: '0 4px 8px -2px rgba(32, 37, 50, 0.24)',
    },

    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: '2px',
    },

    [theme.breakpoints.down('xs')]: {
      minWidth: 0,
    },
  },
  optionSelected: {
    borderWidth: '4px',
    borderColor: theme.palette.primary.main,
  },
  optionTop: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  optionDetails: {
    marginTop: '16px',
    color: theme.palette.darkGray.main,
    fontSize: '14px',
    fontWeight: 600,
  },
}));

const CreateCampaignTypeSelector = () => {
  const classes = useStyles();
  const { user } = useAuthDataContext();
  const {
    availableEventTypes, selectedEventType, setSelectedEventType,
  } = useDataFilterContext();
  const [isOpen, setIsOpen] = React.useState(false);

  const selectableEventTypeNames = [EEventType.FullFarm, EEventType.AbsenteeOwners];
  if (NOD_CAMPAIGN_ENABLED_USER_IDS.includes(user.id)) selectableEventTypeNames.push(EEventType.NOD);

  const selectableEventTypes = selectableEventTypeNames
    .map((eventTypeName) => availableEventTypes.find(({ name }) => name === eventTypeName))
    .filter(Boolean);

  if (!selectedEventType || !selectableEventTypeNames.includes(selectedEventType.name)) return null;

  return (
    <>
      <ButtonBase
        className={classes.trigger}
        onClick={() => setIsOpen(true)}
        aria-label={`Change campaign type. Current type: ${selectedEventType.name}`}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
      >
        <EventTypeBadge eventType={selectedEventType.name} />
        <ArrowDropDownRoundedIcon className={classes.triggerIcon} />
      </ButtonBase>

      <Dialog
        open={isOpen}
        maxWidth="sm"
        onClose={() => setIsOpen(false)}
      >
        <SimpleModalHeader onClose={() => setIsOpen(false)} title="Select Campaign Type" />
        <div className={classes.options}>
          {selectableEventTypes.map((eventType) => {
            const isSelected = eventType._id === selectedEventType._id;

            return (
              <ButtonBase
                key={eventType._id}
                className={cx(classes.option, isSelected && classes.optionSelected)}
                aria-pressed={isSelected}
                onClick={() => {
                  setSelectedEventType(eventType);
                  setIsOpen(false);
                }}
              >
                <div className={classes.optionTop}>
                  <EventTypeBadge eventType={eventType.name} />
                </div>
                <span className={classes.optionDetails}>
                  {`${eventType.campaignLengthInMonths} months / ${eventType.numberOfTouchesPerEvent} touches`}
                </span>
              </ButtonBase>
            );
          })}
        </div>
      </Dialog>
    </>
  );
};

export default CreateCampaignTypeSelector;
